/**
 * screens/Settings/components/SettingsCard
 * The shared shell for all six Settings cards: a `12px 15px` #FAFAFA header
 * with a title, an optional explanatory subtitle and one action, then the
 * body, then an optional footer note in the same #FAFAFA at `11px 15px`.
 *
 * The footer note is not decoration. Each one states the RULE the card cannot
 * express through its controls — "Spends under the auto-approve floor are never
 * held, whatever the rules say" — which is exactly the kind of thing an admin
 * would otherwise have to discover by experiment.
 */
import type { ReactNode } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Card } from '../../../components';
import { neutral } from '../../../theme/colors';
import { sans, size as fontSize, leading } from '../../../theme/typography';

export interface SettingsCardProps {
  title: string;
  subtitle?: string;
  /** The card's single action, or a small group of them. */
  action?: ReactNode;
  children: ReactNode;
  /** The rule this card's controls cannot state. */
  footNote?: string;
}

export function SettingsCard({
  title,
  subtitle,
  action,
  children,
  footNote,
}: SettingsCardProps) {
  return (
    <Card style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.title}>{title}</Text>
          {!!subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
        </View>
        {!!action && <View style={styles.action}>{action}</View>}
      </View>

      {children}

      {!!footNote && (
        <View style={styles.foot}>
          <Text style={styles.footText}>{footNote}</Text>
        </View>
      )}
    </Card>
  );
}

const styles = StyleSheet.create({
  // 16px between Settings cards, from the design source.
  card: { marginBottom: 16 },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 15,
    backgroundColor: neutral.surface2,
    borderBottomWidth: 1,
    borderBottomColor: neutral.border,
  },
  headerText: { flex: 1, minWidth: 0, gap: 2 },
  title: { ...sans('600'), fontSize: fontSize.body, color: neutral.ink },
  subtitle: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
  },
  action: { flexDirection: 'row', gap: 8, flexShrink: 0 },
  foot: { paddingVertical: 11, paddingHorizontal: 15, backgroundColor: neutral.surface2 },
  footText: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
  },
});

export default SettingsCard;
